/**
 * ForceUpdateGate — optimistic update gate.
 *
 * Unlike PrivacyConsentGate (which blocks the router until a flag is read), this
 * renders the app IMMEDIATELY and runs the version check in the background, then
 * OVERLAYS the blocking force screen only if a "force" verdict arrives — so a
 * slow/flaky network can never delay a cold start on an app used at sea.
 *
 *   • forceUpgrade → overlay <ForceUpdateScreen> (blocks everything)
 *   • recommendUpgrade → dismissible prompt, POST-LOGIN only, once per
 *     latestVersion (cooldown persisted so it never nags)
 *
 * Fully independent of PrivacyConsentGate — never reads/writes the privacy flag,
 * so accepting privacy never dismisses this and this never re-asks privacy.
 * Native-only; inert on web. Fail-open: any check failure → no block.
 */
import { useEffect, useRef, useState, type ReactNode } from "react";
import { Capacitor } from "@capacitor/core";
import { App as CapacitorApp } from "@capacitor/app";
import { Preferences } from "@capacitor/preferences";
import { useAuth } from "@/contexts/AuthContext";
import { ConfirmDialog } from "@/components/common/confirm-dialog";
import { setUpgradeRequiredHandler } from "@/lib/api-client";
import { getUpdateStatus, initAppIdentity, openStore, type UpdateStatus } from "@/lib/app-version";
import { ForceUpdateScreen } from "./ForceUpdateScreen";

const RECOMMEND_DISMISS_KEY = "update:recommend-dismissed";

export function ForceUpdateGate({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [status, setStatus] = useState<UpdateStatus | null>(null);
  const [forced, setForced] = useState(false);
  const [recommendOpen, setRecommendOpen] = useState(false);
  const statusRef = useRef<UpdateStatus | null>(null);
  statusRef.current = status;

  // A 426 from ANY request also flips the force screen (hard-floor enforcement).
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;
    setUpgradeRequiredHandler(() => {
      setForced(true);
      // Make sure we have a store URL / message to show.
      if (!statusRef.current) void getUpdateStatus().then(setStatus);
    });
    return () => setUpgradeRequiredHandler(null);
  }, []);

  // Optimistic check on launch + on app resume. Never blocks render.
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;
    let alive = true;
    const run = async () => {
      await initAppIdentity();
      const s = await getUpdateStatus();
      if (!alive) return;
      setStatus(s);
      if (s.forceUpgrade) setForced(true);
    };
    void run();
    const sub = CapacitorApp.addListener("resume", () => { void run(); });
    return () => { alive = false; void sub.then((h) => h.remove()); };
  }, []);

  // Recommend: post-login only, once per latestVersion.
  useEffect(() => {
    if (forced || !user || !status?.recommendUpgrade) return;
    let alive = true;
    void Preferences.get({ key: RECOMMEND_DISMISS_KEY }).then(({ value }) => {
      if (alive && value !== (status.latestVersion ?? "")) setRecommendOpen(true);
    });
    return () => { alive = false; };
  }, [status, forced, user]);

  const dismissRecommend = () => {
    setRecommendOpen(false);
    void Preferences.set({ key: RECOMMEND_DISMISS_KEY, value: status?.latestVersion ?? "" });
  };

  return (
    <>
      {children}
      {forced ? <ForceUpdateScreen status={status} /> : null}
      {recommendOpen && status ? (
        <ConfirmDialog
          open={recommendOpen}
          onOpenChange={(o) => { if (!o) dismissRecommend(); }}
          title="Update available"
          description={
            status.message ??
            `A newer version of iCheck${status.latestVersion ? ` (v${status.latestVersion})` : ""} is available with the latest fixes and improvements.`
          }
          confirmLabel="Update now"
          cancelLabel="Later"
          tone="default"
          onConfirm={() => { openStore(status.storeUrl); dismissRecommend(); }}
        />
      ) : null}
    </>
  );
}
